import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';
import VastuSubHeader from '../components/VastuSubHeader';
import PageBackground from '../components/PageBackground';
import toast from 'react-hot-toast';
import './VastuStyles.css'; // Uses our scoped styles

const vastuServices = [
  'Residential Vastu', 'Apartment & Villa Vastu', 'Office Vastu',
  'Shop & Showroom Vastu', 'Factory & Industrial Vastu', 'Plot Selection',
  'Interior Vastu Consultation', 'Vastu Energy Assessment', 'Online Vastu Consultation',
];

const elementPanels = [
  { name: 'Earth', caption: 'Magnetic Field', className: 'earth', image: `/images/elements/earth-field.jpg` },
  { name: 'Water', caption: 'Flow & Balance', className: 'water', image: `/images/elements/water-flow.jpg` },
  { name: 'Fire', caption: 'Solar Energy', className: 'fire', image: `/images/elements/fire-solar.jpg` },
  { name: 'Air', caption: 'Wind Energy', className: 'air', image: `/images/elements/air-wind.jpg` },
  { name: 'Space', caption: 'Cosmic Radiation', className: 'space', image: `/images/elements/space-cosmic.jpg` },
];

const backgroundUrl = 'https://images.unsplash.com/photo-1558535235-943a5c518b2b?auto=format&fit=crop&w=1920&q=80';

const VastuLaunchPage = () => {
  const [consultants, setConsultants] = useState([]);
  const consultantCarouselRef = useRef(null);
  const elementStripRef = useRef(null);
  const [consultantCarouselState, setConsultantCarouselState] = useState({ atStart: true, atEnd: false });

  const updateConsultantCarouselState = useCallback(() => {
    const carousel = consultantCarouselRef.current;
    if (!carousel) return;
    const scrollLimit = Math.max(carousel.scrollWidth - carousel.clientWidth, 0);
    const atStart = carousel.scrollLeft <= 4;
    const atEnd = scrollLimit <= 4 || carousel.scrollLeft >= scrollLimit - 4;
    setConsultantCarouselState((current) => (
      current.atStart === atStart && current.atEnd === atEnd ? current : { atStart, atEnd }
    ));
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    apiService.getVastuConsultants()
      .then((response) => {
        setConsultants(response.data || []);
      })
      .catch(() => toast.error('Failed to load consultation data.'));
  }, []);

  // --- Carousel Listeners ---
  useEffect(() => {
    const carousel = consultantCarouselRef.current;
    if (!carousel) return undefined;
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateConsultantCarouselState);
    updateConsultantCarouselState();
    carousel.addEventListener('scroll', updateConsultantCarouselState, { passive: true });
    window.addEventListener('resize', updateConsultantCarouselState);
    resizeObserver?.observe(carousel);
    return () => {
      carousel.removeEventListener('scroll', updateConsultantCarouselState);
      window.removeEventListener('resize', updateConsultantCarouselState);
      resizeObserver?.disconnect();
    };
  }, [consultants.length, updateConsultantCarouselState]);

  useEffect(() => {
    const strip = elementStripRef.current;
    if (!strip) return undefined;
    const mobileQuery = window.matchMedia('(max-width: 860px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frameId, restartTimer, lastTimestamp, direction = 1;
    const canAutoPan = () => (mobileQuery.matches && !reducedMotionQuery.matches && strip.scrollWidth > strip.clientWidth + 1);
    const autoPan = (timestamp) => {
      if (!canAutoPan()) return;
      if (!lastTimestamp) lastTimestamp = timestamp;
      const scrollLimit = strip.scrollWidth - strip.clientWidth;
      const nextPosition = strip.scrollLeft + (timestamp - lastTimestamp) * 0.018 * direction;
      if (nextPosition >= scrollLimit) { strip.scrollLeft = scrollLimit; direction = -1; }
      else if (nextPosition <= 0) { strip.scrollLeft = 0; direction = 1; }
      else { strip.scrollLeft = nextPosition; }
      lastTimestamp = timestamp;
      frameId = window.requestAnimationFrame(autoPan);
    };
    const startAutoPan = () => {
      window.cancelAnimationFrame(frameId); window.clearTimeout(restartTimer); lastTimestamp = undefined;
      if (canAutoPan()) frameId = window.requestAnimationFrame(autoPan);
    };
    const pauseAutoPan = () => {
      window.cancelAnimationFrame(frameId); window.clearTimeout(restartTimer);
      restartTimer = window.setTimeout(startAutoPan, 2500);
    };
    startAutoPan();
    strip.addEventListener('pointerdown', pauseAutoPan); strip.addEventListener('touchstart', pauseAutoPan, { passive: true }); strip.addEventListener('wheel', pauseAutoPan, { passive: true });
    window.addEventListener('resize', startAutoPan); mobileQuery.addEventListener('change', startAutoPan); reducedMotionQuery.addEventListener('change', startAutoPan);
    return () => {
      window.cancelAnimationFrame(frameId); window.clearTimeout(restartTimer);
      strip.removeEventListener('pointerdown', pauseAutoPan); strip.removeEventListener('touchstart', pauseAutoPan); strip.removeEventListener('wheel', pauseAutoPan);
      window.removeEventListener('resize', startAutoPan); mobileQuery.removeEventListener('change', startAutoPan); reducedMotionQuery.removeEventListener('change', startAutoPan);
    };
  }, []);

  const visibleConsultants = consultants.filter((c) => (c.consultationTypes || []).some((t) => t.isActive));
  const getInitials = (name = '') => name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase() || 'VA';

  const scrollConsultantProfiles = (direction) => {
    const carousel = consultantCarouselRef.current;
    if (!carousel) return;
    carousel.scrollBy({ left: direction * Math.max(carousel.clientWidth * 0.78, 360), behavior: 'smooth' });
  };

  return (
    <div className="vastu-page vastu-home-page">
      <PageBackground imageUrl={backgroundUrl} />
      <VastuSubHeader />

      {/* Full-width Hero Band */}
      <section className="vastu-hero-band">
        <div className="vastu-hero-content">
          <p className="eyebrow">Expert Vastu Consultation</p>
          <h1>Upload your house plan and consult a verified expert.</h1>
          <p>Anandmaya connects you with top consultants for in-depth plan reviews, video guidance, personalized remedies, and comprehensive final reports.</p>
        </div>
      </section>

      {/* Full-width Element Banner */}
      <section className="vastu-element-banner">
        <div ref={elementStripRef} className="element-panel-strip" aria-hidden="true">
          {elementPanels.map((element) => (
            <span key={element.name} className={`element-panel ${element.className}`} style={{ '--element-image': `url("${element.image}")` }}>
              <strong>{element.name}</strong>
              <small>{element.caption}</small>
            </span>
          ))}
        </div>
        <div className="element-banner-content" aria-hidden="true"></div>
      </section>

      <div className="vastu-main-content-wrapper">

        {/* Services & Introduction */}
        <section className="vastu-intro-section">
          <div className="vastu-introduction">
            <h2>Spaces shaped with purpose and balance</h2>
            <p className="vastu-intro-lead">Every great journey begins with the right foundation. Your home is where your family grows, and your workplace is where your aspirations take shape. According to the timeless principles of Vastu Shastra, a thoughtfully planned space can foster harmony, comfort, and efficient use of your environment. Whether for a home, office, or commercial establishment, Vastu offers practical guidance for creating functional and harmonious surroundings.</p>
          </div>
          <aside className="home-services-panel">
            <p className="eyebrow">Our Services</p>
            <ul className="home-services-list">
              {vastuServices.map((service) => <li key={service}>{service}</li>)}
            </ul>
            <Link className="service-consultation-link" to="/vastu/book">Request Consultation</Link>
          </aside>
        </section>

        {/* Consultant Carousel */}
        {visibleConsultants.length > 0 && (
          <section className="consultant-carousel-section">
            <div className="section-heading" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h2 style={{ margin: 0 }}>Meet our verified experts</h2>
              <Link to="/vastu/consultants" className="file-link-btn" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                View All Experts <i className="fas fa-arrow-right"></i>
              </Link>
            </div>

            <div className="consultant-carousel-shell">
              <div className="consultant-carousel-controls">
                <button type="button" className="consultant-carousel-control" onClick={() => scrollConsultantProfiles(-1)} disabled={consultantCarouselState.atStart}><span aria-hidden="true">&larr;</span></button>
                <button type="button" className="consultant-carousel-control" onClick={() => scrollConsultantProfiles(1)} disabled={consultantCarouselState.atEnd}><span aria-hidden="true">&rarr;</span></button>
              </div>

              <div
                ref={consultantCarouselRef}
                className="consultant-profile-grid-home consultant-carousel"
                role="region"
                aria-label="Available Vastu consultant profiles"
                tabIndex="0"
              >
                {visibleConsultants.map((consultant) => (
                  <article className="premium-consultant-card" key={consultant._id}>
                    <div className="card-top-section">
                      <div className="consultant-avatar-large">
                        {consultant.profilePhoto?.storagePath ? (
                          <img src={consultant.profilePhoto.storagePath} alt={consultant.displayName} />
                        ) : (
                          <span>{getInitials(consultant.displayName)}</span>
                        )}
                      </div>
                      <div className="consultant-primary-info">
                        <h3>{consultant.displayName}</h3>
                        <p className="consultant-headline">{consultant.profileHeadline || 'Expert Vastu Consultant'}</p>
                        <div className="consultant-rating">
                          <i className="fas fa-star"></i>
                          <span>{consultant.ratingAverage || 5.0}</span>
                          <span className="exp-badge">{consultant.experienceYears || 0}+ Yrs Exp</span>
                        </div>
                      </div>
                    </div>

                    <div className="card-middle-section">
                      <h4>About</h4>
                      <p className="consultant-bio-full">
                        {consultant.bio || 'Dedicated to bringing harmony and prosperity to your spaces through authentic Vastu principles.'}
                      </p>
                    </div>

                    <div className="card-bottom-section">
                      <div className="skills-tags">
                        {(consultant.specializations || []).slice(0, 3).map((item) => (
                          <span key={item}><i className="fas fa-check-circle"></i> {item}</span>
                        ))}
                      </div>
                      <div className="lang-info">
                        <i className="fas fa-globe"></i> {(consultant.languages || []).slice(0, 2).join(', ') || 'Hindi, English'}
                      </div>
                    </div>
                    
                    {/* --- ADDED LINK BUTTON AT THE BOTTOM OF THE CARD --- */}
                    <div style={{ marginTop: '1.5rem', borderTop: '1px solid #eee', paddingTop: '1rem' }}>
                      <Link 
                        to={`/vastu/consultants/${consultant._id}`} 
                        className="secondary-action-btn" 
                        style={{ display: 'block', textAlign: 'center', textDecoration: 'none', color: 'var(--vastu-green)', borderColor: 'var(--vastu-green)', background: 'transparent' }}
                      >
                        View Full Profile
                      </Link>
                    </div>
                    {/* -------------------------------------------------------- */}
                  </article>
                ))}
              </div>

            </div>
          </section>
        )}

        {/* --- NEW CTA SECTION --- */}
        <section className="vastu-cta-section" style={{ textAlign: 'center', padding: '1rem 1rem', background: 'white', borderRadius: '12px', border: '1px solid #f0ece6', boxShadow: '0 10px 30px rgba(0,0,0,0.03)' }}>
            <h2 style={{ fontFamily: 'var(--heading-font)', color: 'var(--vastu-deep)', fontSize: '2.5rem', marginBottom: '1rem' }}>Ready to align your space?</h2>
            <p style={{ color: '#555', fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto 2rem auto' }}>
              Upload your floor plan and get actionable, personalized remedies from our verified Vastu experts to bring prosperity and peace into your home or office.
            </p>
            <Link to="/vastu/book" className="primary-btn" style={{ fontSize: '1.2rem', padding: '1rem 3rem', borderRadius: '30px' }}>
              <i className="fas fa-file-upload"></i> Start Consultation Request
            </Link>
        </section>
        {/* ---------------------------------------------------- */}

      </div>
    </div>
  );
};

export default VastuLaunchPage;